import { useQuery, useMutation } from "@apollo/client";
import {
  ADMIN_MARKETPLACE_ITEMS,
  ALL_ORDERS,
  ORGANIZATION_MARKETPLACE_ITEMS,
  ORGANIZATION_ORDERS,
  ORGANIZATION_ORDER_STATS,
  CREATE_MARKETPLACE_ITEM,
  UPDATE_MARKETPLACE_ITEM,
  DEACTIVATE_MARKETPLACE_ITEM,
  FULFILL_ORDER,
  CANCEL_ORDER,
} from "../graphql";
import type {
  MarketplaceItemsResult,
  MarketplaceOrdersResult,
  MarketplaceCategory,
  OrderStatus,
  OrganizationOrderStats,
} from "../types";

// ─── Item Queries ────────────────────────────────────────────────────────────

interface UseMarketplaceItemsOptions {
  search?: string;
  category?: MarketplaceCategory;
  featured?: boolean;
  limit?: number;
  offset?: number;
}

export function useAdminMarketplaceItems(options: UseMarketplaceItemsOptions = {}) {
  const { search, category, featured, limit = 50, offset = 0 } = options;
  return useQuery<{ adminMarketplaceItems: MarketplaceItemsResult }>(
    ADMIN_MARKETPLACE_ITEMS,
    {
      variables: {
        input: {
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(category ? { category } : {}),
          ...(typeof featured === "boolean" ? { featured } : {}),
        },
      },
      fetchPolicy: "cache-and-network",
    },
  );
}

interface UseOrgMarketplaceItemsOptions {
  organizationId: string;
  search?: string;
  category?: MarketplaceCategory;
  featured?: boolean;
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
}

export function useOrganizationMarketplaceItems(
  options: UseOrgMarketplaceItemsOptions,
) {
  const {
    organizationId,
    search,
    category,
    featured,
    includeInactive,
    limit = 50,
    offset = 0,
  } = options;
  return useQuery<{
    organizationMarketplaceItems: MarketplaceItemsResult;
  }>(ORGANIZATION_MARKETPLACE_ITEMS, {
    variables: {
      input: {
        organizationId,
        limit,
        offset,
        ...(search ? { search } : {}),
        ...(category ? { category } : {}),
        ...(typeof featured === "boolean" ? { featured } : {}),
        ...(includeInactive ? { includeInactive } : {}),
      },
    },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
  });
}

// ─── Order Queries ───────────────────────────────────────────────────────────

interface UseAllOrdersOptions {
  status?: OrderStatus;
  limit?: number;
  offset?: number;
}

export function useAllOrders(options: UseAllOrdersOptions = {}) {
  const { status, limit = 50, offset = 0 } = options;
  return useQuery<{ allOrders: MarketplaceOrdersResult }>(ALL_ORDERS, {
    variables: {
      input: {
        limit,
        offset,
        ...(status ? { status } : {}),
      },
    },
    fetchPolicy: "cache-and-network",
  });
}

interface UseOrgOrdersOptions {
  organizationId: string;
  status?: OrderStatus;
  limit?: number;
  offset?: number;
}

export function useOrganizationOrders(options: UseOrgOrdersOptions) {
  const { organizationId, status, limit = 50, offset = 0 } = options;
  return useQuery<{ organizationOrders: MarketplaceOrdersResult }>(
    ORGANIZATION_ORDERS,
    {
      variables: {
        input: {
          organizationId,
          limit,
          offset,
          ...(status ? { status } : {}),
        },
      },
      skip: !organizationId,
      fetchPolicy: "cache-and-network",
    },
  );
}

export function useOrganizationOrderStats(organizationId: string) {
  return useQuery<{
    organizationOrderStats: OrganizationOrderStats;
  }>(ORGANIZATION_ORDER_STATS, {
    variables: { organizationId },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
  });
}

// ─── Item Mutations ──────────────────────────────────────────────────────────

export function useCreateMarketplaceItem() {
  return useMutation(CREATE_MARKETPLACE_ITEM, {
    refetchQueries: ["AdminMarketplaceItems", "OrganizationMarketplaceItems"],
  });
}

export function useUpdateMarketplaceItem() {
  return useMutation(UPDATE_MARKETPLACE_ITEM, {
    refetchQueries: ["AdminMarketplaceItems", "OrganizationMarketplaceItems"],
  });
}

export function useDeactivateMarketplaceItem() {
  return useMutation(DEACTIVATE_MARKETPLACE_ITEM, {
    refetchQueries: ["AdminMarketplaceItems", "OrganizationMarketplaceItems"],
  });
}

// ─── Order Mutations ─────────────────────────────────────────────────────────

export function useFulfillOrder() {
  return useMutation(FULFILL_ORDER, {
    refetchQueries: ["AllOrders", "OrganizationOrders", "OrganizationOrderStats"],
  });
}

export function useCancelOrder() {
  return useMutation(CANCEL_ORDER, {
    refetchQueries: ["AllOrders", "OrganizationOrders", "OrganizationOrderStats"],
  });
}
